var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { fsModalDialogDefaults } from '@cof/fs-modal-dialog-core';
import modalDialogStyles from '@cof/fs-modal-dialog-core/lib/modal-dialog.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
/**
 * The ModalDialogFooter contains the main actions for the modal dialog. It can be composed with standard button
 * groupings or slotted content with other components and custom elements.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-modal-dialog-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-modal-dialog-footer` call
 *
 * ```javascript
 * import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
 * ```
 *
 * To import the `FsModalDialog` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsModalDialogFooter } from '@cof/fs-modal-dialog-wc';
 * ```
 *
 * ### Footer action buttons
 * Footer action buttons are _intentionally uncontrolled_. The implementer is responsible for the result of any user
 * interaction with the footer actions. This also applies to closing the ModalDialog. If you need to close the
 * ModalDialog in response to user interaction, we suggest calling the public `closeModalDialog` method.
 *
 * ## Anatomy
 * ### DOM structure
 * ```html
 * <fs-modal-dialog>
 *   <button id="open">Open Modal</button>
 *   <h2 slot="modal-dialog-header">Modal Dialog</h2>
 *   <p class="fs-body-text fs-body-text--normal" slot="modal-dialog-body">Body content...</p>
 *   <fs-modal-dialog-footer>
 *     <button @click=${// Implementer logic here...} class="fs-button fs-button--primary">Acknowledge</button>
 *     <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Cancel</button>
 *   </fs-modal-dialog-footer>
 * </fs-modal-dialog>
 * ```
 *
 * ### Footer composition
 *
 * The footer of the ModalDialog will automatically apply some layout to slotted in buttons, but it is the responsibility
 * of the developer to properly assign visual hierarchical representation to said buttons.
 *
 * Some common compositions can be seen below:
 *
 * #### Three-action footer (side-by-side)
 *
 * ```html
 * ...
 * <fs-modal-dialog-footer>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--primary">Primary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Secondary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--primary fs-button--ghost">Tertiary</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * #### Two-action footer (side-by-side)
 *
 * ```html
 * ...
 * <fs-modal-dialog-footer>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--primary">Primary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Secondary</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * #### One-action footer (side-by-side)
 *
 * ```html
 * ...
 * <fs-modal-dialog-footer>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--primary">Primary</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * #### Destructive Three-action footer (side-by-side)
 *
 * ```html
 * ...
 * <fs-modal-dialog-footer>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--destructive">Primary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Secondary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--primary fs-button--ghost">Tertiary</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * #### Destructive Two-action footer (side-by-side)
 *
 * ```html
 * ...
 * <fs-modal-dialog-footer>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--destructive">Primary</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Secondary</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * ### Stacking
 * Whenever possible, the footer actions should be displayed inline, not stacked. There are however situations in which
 * the text content of the buttons will make them too large to properly fit in the modal dialog window on smaller
 * breakpoints.
 *
 * For example, if the content of the footer action buttons is too large to fit them all inline below the "small"
 * breakpoint, the footer can be configured to break into a stacked layout below the "small" breakpoint like so:
 * ```html
 * <fs-modal-dialog-footer footer-layout-swap-breakpoint="small">
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--destructive">Long long long long long</button>
 *   <button @click=${// Implementer logic here...} class="fs-button fs-button--secondary fs-button--outlined">Long long long long long</button>
 * </fs-modal-dialog-footer>
 * ```
 *
 * @element fs-modal-dialog-footer
 * @slot - the default slot transcludes any "actions" that should reside in the footer of the ModalDialog
 */
export class FsModalDialogFooter extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The "footer layout" of the ModalDialog.
         * This controls how "actions" (buttons) in the footer of the ModalDialog are arranged.
         */
        this.footerLayoutSwapBreakpoint = fsModalDialogDefaults.footerLayoutSwapBreakpoint;
        /**
         * The slot this element should participate in. This should usually not be touched by a developer.
         */
        this.slot = 'modal-dialog-footer';
    }
    render() {
        return html `
      <div
        class="
          fs-modal-dialog__footer
          ${!this.footerLayoutSwapBreakpoint
            ? ''
            : `fs-modal-dialog__footer--break-at-${this.footerLayoutSwapBreakpoint}`}
        "
      >
        <slot></slot>
      </div>
    `;
    }
}
FsModalDialogFooter.canonicalName = 'modal-dialog-footer';
FsModalDialogFooter.styles = [
    modalDialogStyles,
    css `
      :host {
        display: block;
        width: 100%;
      }
    `
];
__decorate([
    property({ type: String, attribute: 'footer-layout-swap-breakpoint' })
], FsModalDialogFooter.prototype, "footerLayoutSwapBreakpoint", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsModalDialogFooter.prototype, "slot", void 0);
//# sourceMappingURL=ModalDialogFooter.js.map